import { viewport } from './viewport';

export class SliderBox {
    /**
     * @param {Object} config SlideBox configuration.
     * @param {HTMLElement} config.element SlideBox reference HTML element.
     * @param {Number} config.animateDuration Duration of slide animation in milliseconds.
     * @param {Boolean} config.autoSlideShow Enable or disable automatic slide show.
     * @param {Number} config.maxTotalPages Maximum number of pagination page items. Zero for disable.
     * @param {Boolean} config.showNavButtons Whether show navigation buttons or not.
     * @param {Boolean} config.showPagination Whether show pagination or not.
     * @param {Number} config.slideShowDelay Delay time in milliseconds before slide to next item when automatic slideshow is enabled.
     * @param {Boolean} config.slideShowInfinity If true, repeat the slideshow infinitely after reach to the end of the slider box in automatic mode.
     */
    constructor({
        element,
        animateDuration = 1000,
        autoSlideShow = true,
        maxTotalPages = 7,
        showNavButtons = false,
        showPagination = true,
        slideShowDelay = 4000,
        slideShowInfinity = true,
    }) {
        this.element = element;
        this.animateDuration = animateDuration;
        this.autoSlideShow = autoSlideShow;
        this.maxTotalPages = maxTotalPages;
        this.showNavButtons = showNavButtons;
        this.showPagination = showPagination;
        this.slideShowDelay = slideShowDelay;
        this.slideShowInfinity = slideShowInfinity;

        this._innerElement = null;
        this._itemsContainer = null;
        this._items = null;
        this._viewportNumItems = null;
        this._itemWidth = null;
        this._totalPages = null;
        this._itemsPerPage = null;
        this._minSlideLength = null;
        this._maxSlideLength = 0;
        this._navContainer = null;
        this._navPrev = null;
        this._navNext = null;
        this._pagination = null;
        this._paginationItems = null;
        this._intersectionObserver = null;

        this.currentPage = 0;
        this.touchData = null;
        this.lastSlideLength = 0;
        this.slideShowRequestId = null;
        this.slideShowStartTime = 0;
        this.isMouseSwiping = false;
        this.wheelData = null;

        this.init();
    }

    /**
     * @returns {Element|undefined} SlideBox inner element
     */
    get innerElement() {
        if (this._innerElement === null) {
            this._innerElement = this.element?.firstElementChild;
            if (!this._innerElement) {
                this._innerElement = undefined;
            }
        }
        return this._innerElement;
    }

    /**
     * @returns {Promise<Number>} Inner element's width
     */
    get innerWidth() {
        return new Promise((resolve, reject) => {
            try {
                if (this.innerElement) {
                    requestAnimationFrame(() => {
                        resolve(this.innerElement.clientWidth);
                    });
                } else {
                    resolve(0);
                }
            } catch (error) {
                reject(error);
            }
        });
    }

    /**
     * @returns {HTMLElement|undefined} SlideBox items' container
     */
    get itemsContainer() {
        if (this._itemsContainer === null) {
            this._itemsContainer = this.innerElement?.firstElementChild;
            if (!this._itemsContainer || !this._itemsContainer.hasAttribute('slider-items')) {
                this._itemsContainer = undefined;
            }
        }
        return this._itemsContainer;
    }

    /**
     * @returns {HTMLElement[]} SlideBox items
     */
    get items() {
        if (this._items === null) {
            const items = this.itemsContainer?.querySelectorAll('[slider-item]');
            if (items) {
                this._items = Array.from(items);
            } else {
                this._items = [];
            }
        }
        return this._items;
    }

    /**
     * @returns {Number}
     */
    get itemsCount() {
        return this.items.length;
    }

    /**
     * @returns {Promise<Number>} Number of appearing items in the viewport.
     */
    get viewportNumItems() {
        if (this._viewportNumItems === null) {
            return Promise.resolve(this.computeViewportNumItems()).then(value => {
                this._viewportNumItems = value;
                return value;
            });
        }

        return Promise.resolve(this._viewportNumItems);
    }

    /**
     * @returns {Promise<Number>} Width of one slide item.
     */
    get itemWidth() {
        if (this._itemWidth === null) {
            return Promise.resolve(this.computeItemWidth()).then(value => {
                this._itemWidth = value;
                return value;
            });
        }

        return Promise.resolve(this._itemWidth);
    }

    /**
     * @returns {Promise<Number>} Number of pages.
     */
    get totalPages() {
        if (this._totalPages === null) {
            return Promise.resolve(this.computeTotalPages()).then(value => {
                this._totalPages = value;
                return value;
            });
        }

        return Promise.resolve(this._totalPages);
    }

    /**
     * @returns {Promise<Number>}
     */
    get itemsPerPage() {
        if (this._itemsPerPage === null) {
            return Promise.resolve(this.computeItemsPerPage()).then(value => {
                this._itemsPerPage = value;
                return value;
            });
        }

        return Promise.resolve(this._itemsPerPage);
    }

    /**
     * @returns {HTMLElement|undefined}
     */
    get navContainer() {
        if (this._navContainer === null) {
            this._navContainer = this.innerElement?.lastElementChild;
            if (!this._navContainer || !this._navContainer.hasAttribute('slider-nav')) {
                this._navContainer = undefined;
            }
        }
        return this._navContainer;
    }

    get navPrev() {
        if (this._navPrev === null) {
            const prev = document.createElement('a');

            prev.setAttribute('href', '#');
            prev.innerText = 'Prev';

            prev.addEventListener('click', (event) => {
                event.preventDefault();
                this.handleNavPrev();
            });

            this._navPrev = prev;
        }

        return this._navPrev;
    }

    get navNext() {
        if (this._navNext === null) {
            const next = document.createElement('a');

            next.setAttribute('href', '#');
            next.innerText = 'Next';

            next.addEventListener('click', (event) => {
                event.preventDefault();
                this.handleNavNext();
            });

            this._navNext = next;
        }

        return this._navNext;
    }

    /**
     *
     * @returns {Promise<HTMLElement>}
     */
    get pagination() {
        if (this._pagination === null) {
            return Promise.resolve(this.paginationItems).then(paginationItems => {

                const _pagination = document.createElement('div');
                _pagination.setAttribute('slider-pagination', '');
                _pagination.append(...paginationItems);

                this._pagination = _pagination;

                return _pagination;
            });
        }

        return Promise.resolve(this._pagination);
    }

    /**
     * @returns {Promise<HTMLElement[]>}
     */
    get paginationItems() {
        if (this._paginationItems === null) {
            return Promise.resolve(this.computePaginationItems()).then(items => {
                this._paginationItems = items;
                return items;
            });
        }

        return Promise.resolve(this._paginationItems);
    }

    get intersectionObserver() {
        if (this._intersectionObserver === null) {
            const options = {
                root: null,  // Default is the viewport.
                rootMargin: '0px',
                threshold: 0.1,
            };

            this._intersectionObserver = new IntersectionObserver((entries) => {
                this.handleIntersectionObserver(entries);
            }, options);
        }
        return this._intersectionObserver;
    }

    /**
     * @returns {Boolean}
     */
    get isSwiping() {
        return !!(this.touchData && this.touchData.startPoint);
    }

    /**
     * @returns {Boolean}
     */
    get isSwipingLeft() {
        return this.isSwiping && this.touchData.direction.toLowerCase() === 'left';
    }

    /**
     * @returns {Boolean}
     */
    get isSwipingRight() {
        return this.isSwiping && this.touchData.direction.toLowerCase() === 'right';
    }

    /**
     * @returns {Number}
     */
    get swipingSlideLength() {
        let swipingDistance = 0;

        if (this.isSwiping) {
            swipingDistance = parseInt(this.touchData.distance);

            if (Number.isInteger(swipingDistance)) {
                if (this.isSwipingLeft) {
                    swipingDistance *= -1;
                }
            } else {
                swipingDistance = 0;
            }
        }

        return swipingDistance + this.lastSlideLength;
    }

    /**
     * @returns {Promise<Number>}
     */
    get minSlideLength() {
        if (this._minSlideLength === null) {
            return Promise.resolve(this.computeMinSlideLength()).then(value => {
                this._minSlideLength = value;
                return value;
            });
        }

        return Promise.resolve(this._minSlideLength);
    }

    /**
     * @returns {Number}
     */
    get maxSlideLength() {
        return this._maxSlideLength;
    }

    /**
     * @returns {Boolean}
     */
    get isWheeling() {
        return !!(this.wheelData && this.wheelData.currentEvent);
    }

    /**
     * @returns {Boolean}
     */
    get isWheelingLeft() {
        return this.isWheeling && this.wheelData.direction.toLowerCase() === 'left';
    }

    /**
     * @returns {Boolean}
     */
    get isWheelingRight() {
        return this.isWheeling && this.wheelData.direction.toLowerCase() === 'right';
    }

    /**
     * @returns {Promise<Number>}
     */
    get wheelingSlideLength() {
        return Promise.resolve(this.minSlideLength).then(minSlideLength => {
            const maxSlideLength = this.maxSlideLength;
            let wheelingDistance = 0;
            let _wheelingSlideLength = this.lastSlideLength;

            if (this.isWheeling) {
                wheelingDistance = parseInt(this.wheelData.distance);

                if (Number.isInteger(wheelingDistance)) {
                    if (this.isWheelingLeft) {
                        wheelingDistance *= -1;
                    }
                } else {
                    wheelingDistance = 0;
                }
            }

            if (wheelingDistance !== 0) {
                _wheelingSlideLength += wheelingDistance;


                if (_wheelingSlideLength < minSlideLength) {
                    _wheelingSlideLength = minSlideLength;
                } else if (_wheelingSlideLength > maxSlideLength) {
                    _wheelingSlideLength = maxSlideLength;
                }
            }

            return _wheelingSlideLength;
        });
    }

    /**
     * Initialize the SliderBox state.
     */
    async init() {
        await this.registerEvents();

        if (this.autoSlideShow) {
            this.intersectionObserver.observe(this.element);
        }

        viewport.onResize(() => {
            this.onViewportResize();
        });

        if (!!this.navContainer) {
            if (this.showNavButtons) {
                requestAnimationFrame(() => {
                    this.navContainer.append(this.navPrev, this.navNext);
                });
            }

            if (this.showPagination) {
                const pagination = await this.pagination;
                requestAnimationFrame(() => {
                    this.navContainer.appendChild(pagination);
                });
            }
        }

        await this.updateItemsWidth();
    }

    async registerEvents() {
        if (this.itemsContainer) {
            const isTouchScreen = viewport.isTouchScreen;
            const isMobileDevice = viewport.isMobileDevice;

            const onTouchEventListener = (touchEvent) => {
                this.handleTouchEvent(touchEvent);
            };

            const onMouseDragEventListener = (mouseEvent) => {
                this.handleMouseDragEvent(mouseEvent);
            };

            const onWheelEventListener = (wheelEvent) => {
                //
                // Handler for Laptop Track-pad
                //
                this.handleWheelEvent(wheelEvent);
            }

            if (isTouchScreen) {
                this.itemsContainer.addEventListener('touchstart', onTouchEventListener);
                this.itemsContainer.addEventListener('touchmove', onTouchEventListener);
                this.itemsContainer.addEventListener('touchend', onTouchEventListener);
                this.itemsContainer.addEventListener('touchcancel', onTouchEventListener);
            }

            if (!isMobileDevice) {
                this.itemsContainer.addEventListener('mousedown', onMouseDragEventListener);
                this.itemsContainer.addEventListener('mousemove', onMouseDragEventListener);
                this.itemsContainer.addEventListener('mouseleave', onMouseDragEventListener);
                this.itemsContainer.addEventListener('mouseup', onMouseDragEventListener);
                this.itemsContainer.addEventListener('click', onMouseDragEventListener);

                this.itemsContainer.addEventListener('wheel', onWheelEventListener);
            }
        }
    }

    async onViewportResize() {
        await this.refreshViewportNumItems();
        await this.refreshItemWidth();
        await this.refreshTotalPages();
        await this.refreshItemsPerPage();
        await this.refreshPaginationItems();
        await this.refreshMinSlideLength();
        await this.refreshLastSlideLength();
    }

    async refreshViewportNumItems() {
        this._viewportNumItems = await this.computeViewportNumItems();
    }

    async refreshItemWidth() {
        const _itemWidth = await this.computeItemWidth();
        if (this._itemWidth !== _itemWidth) {
            this._itemWidth = _itemWidth;
            await this.updateItemsWidth();
        }
    }

    async refreshTotalPages() {
        this._totalPages = await this.computeTotalPages();
    }

    async refreshItemsPerPage() {
        this._itemsPerPage = await this.computeItemsPerPage();
    }

    async refreshPaginationItems() {
        const pagination = await this.pagination;
        const newPaginationItems = await this.computePaginationItems();
        requestAnimationFrame(() => {
            // Empty the pagination.
            pagination.replaceChildren();
            // Set new items.
            pagination.append(...newPaginationItems);
            this._paginationItems = newPaginationItems;
        });
    }

    async refreshMinSlideLength() {
        this._minSlideLength = await this.computeMinSlideLength();
    }

    async refreshLastSlideLength() {
        this.slide(0);
        this.lastSlideLength = 0;
    }

    async updateItemsWidth() {
        const itemWidth = await this.itemWidth;
        const itemWidthPx = `${itemWidth}px`;

        requestAnimationFrame(() => {
            this.items.forEach(item => {
                item.style.width = itemWidthPx;
            });
        });
    }

    /**
     * Compute viewport num items.
     *
     * @returns {Promise<Number>} Number of appearing items in the viewport
     */
    async computeViewportNumItems() {
        const viewportWidth = await viewport.innerWidth;

        if (viewportWidth < 576) {
            // On mobile devices, show 1 item in viewport.
            return 1;
        } else if (viewportWidth < 992) {
            return 2;
        } else if (viewportWidth < 1200) {
            return 3;
        } else {
            return 4;
        }
    }

    /**
     * Compute the width of one slide item.
     *
     * @returns {Promise<Number>} With of one slide item.
     */
    async computeItemWidth() {
        const innerWidth = await this.innerWidth;
        const viewportNumItems = await this.viewportNumItems;

        const itemWidth = innerWidth / viewportNumItems;

        return itemWidth;
    }

    /**
     * Compute total of pages.
     *
     * @returns {Promise<Number>} Number of pages.
     */
    async computeTotalPages() {
        const viewportNumItems = await this.viewportNumItems;
        const itemsCount = this.itemsCount;

        let totalPages = Math.ceil(itemsCount / viewportNumItems);
        if (totalPages > this.maxTotalPages && this.maxTotalPages > 0) {
            totalPages = this.maxTotalPages;
        }

        return totalPages;
    }

    /**
     * Compute number of items per page.
     *
     * @returns {Promise<Number>} Number of items per page.
     */
    async computeItemsPerPage() {
        const totalPages = await this.totalPages;
        const itemsCount = this.itemsCount;

        const itemsPerPage = Math.ceil(itemsCount / totalPages);

        return itemsPerPage;
    }

    /**
     * Create new pagination items.
     *
     * @returns {Promise<HTMLElement[]>} Pagination items.
     */
    async computePaginationItems() {
        const totalPages = await this.totalPages;
        const refUrl = `#${this.element.id}`;
        const itemEventListener = (event) => {
            event.preventDefault();
            this.handlePagination(event.currentTarget);
        };
        const items = [];

        for (let i = 0; i < totalPages; i++) {
            const pageTitle = `Page ${i + 1}`;

            const item = document.createElement('a');
            item.setAttribute('slider-page-number', '');
            if (i === this.currentPage) {
                item.setAttribute('current', '');
            }
            item.setAttribute('href', refUrl);
            item.dataset.page = i;
            item.setAttribute('title', pageTitle);
            item.innerText = pageTitle;
            item.addEventListener('click', itemEventListener);

            items.push(item);
        }

        return items;
    }

    /**
     * Compute min slide length.
     *
     * @returns {Promise<Number>} Min slide length.
     */
    async computeMinSlideLength() {
        const itemWidth = await this.itemWidth;
        const viewportNumItems = await this.viewportNumItems;
        const itemsCount = this.itemsCount;

        const slideLength = -1 * itemWidth * (itemsCount - viewportNumItems);

        return slideLength;
    }

    async handleNavPrev() {
        console.log('Clicked on prev button');
    }

    async handleNavNext() {
        console.log('Clicked on next button');
    }

    async setActivePaginationItem(pageNumber = 0) {
        if (this.showPagination && pageNumber !== this.currentPage) {
            const paginationItems = await this.paginationItems;
            const currentAttr = 'current';

            requestAnimationFrame(() => {
                paginationItems.forEach(item => {
                    const itemPageNumber = parseInt(item.dataset.page);
                    if (itemPageNumber === pageNumber) {
                        item.setAttribute(currentAttr, '');
                    } else {
                        item.removeAttribute(currentAttr);
                    }
                });
            });

            this.currentPage = pageNumber;
        }
    }

    /**
     * @param {HTMLElement} pageItem
     * @returns {void}
     */
    async handlePagination(pageItem) {
        if (!pageItem) {
            return false;
        }

        let pageNumber = parseInt(pageItem.dataset.page);
        if (Number.isNaN(pageNumber) || pageNumber < 0) {
            pageNumber = 0;
        }

        if (pageNumber !== this.currentPage) {
            const itemWidth = await this.itemWidth;
            const itemsPerPage = await this.itemsPerPage;

            const slideLength = -1 * pageNumber * itemWidth * itemsPerPage;

            await this.stopSlideShow();
            this.slide(slideLength);
            await this.setActivePaginationItem(pageNumber);
        }
    }

    /**
     * @param {Number} length
     * @param {Boolean} finished
     * @param {Boolean} animated
     * @returns {Number}
     */
    slide(length, finished = true, animated = true) {
        //
        // Only run if has any changes.
        //
        if (length !== this.lastSlideLength) {
            const cssText = [
                `transform: translate3d(${length}px, 0, 0)`,
            ];

            if (animated) {
                cssText.push(`transition-duration: ${this.animateDuration}ms`);
            } else {
                cssText.push('transition-duration: 0ms');
            }

            requestAnimationFrame(() => {
                this.itemsContainer.style.cssText = cssText.join(';');
            });

            if (finished) {
                // Update the last slide length
                // when slide action was completed.
                this.lastSlideLength = length;
            }
        }

        return this.lastSlideLength;
    }

    async handleTouchEvent(touchEvent) {

        if (!touchEvent) {
            return;
        }

        const touchList = touchEvent.touches;

        //
        // We only work on ONE FINGER touch
        //
        if (touchList.length > 1) {
            return;
        }

        const touchPoint = touchList.item(0);

        switch (touchEvent.type) {
            case 'touchstart':
                this.updateTouchData(touchPoint);
                break;
            case 'touchmove':
                this.updateTouchData(touchPoint);
                this.onTouchMoving();
                break;
            case 'touchend':
                await this.onTouchEnd();
                break;
            case 'touchcancel':
                await this.onTouchEnd();
                break;
            default:
                break;
        }
    }

    async handleMouseDragEvent(mouseEvent) {
        if (!mouseEvent) {
            return;
        }

        let preventDefault = false;

        const mousePoint = {
            clientX: mouseEvent.clientX,
            clientY: mouseEvent.clientY,
        };

        switch (mouseEvent.type) {
            case 'mousedown':
                preventDefault = true;
                this.updateTouchData(mousePoint);
                break;
            case 'mousemove':
                if (this.isSwiping) {
                    this.isMouseSwiping = true;
                    preventDefault = true;
                    this.updateTouchData(mousePoint);
                    this.onTouchMoving();
                }
                break;
            case 'mouseup':
            case 'mouseleave':
                if (this.isSwiping) {
                    preventDefault = true;
                    this.updateTouchData(mousePoint);
                    await this.onTouchEnd();
                }
                break;
            case 'click':
                // Click event was fired after mouseup.
                // So, we need a additional handle.
                if (this.isMouseSwiping) {
                    preventDefault = true;
                    this.isMouseSwiping = false;
                }
                break;
            default:
                break;
        }

        if (preventDefault) {
            mouseEvent.preventDefault();
        }
    }

    async handleWheelEvent(wheelEvent) {
        try {
            if (!wheelEvent || !wheelEvent instanceof WheelEvent) {
                return;
            }

            this.updateWheelData(wheelEvent);

            if (!this.isWheelingLeft && !this.isWheelingRight) {
                return;
            }

            wheelEvent.preventDefault();

            // Stop the Slideshow if it is running
            // when the client start the swipe action.
            await this.stopSlideShow();

            const itemsPerPage = await this.itemsPerPage;
            const itemWidth = await this.itemWidth;
            const wheelingSlideLength = await this.wheelingSlideLength;

            this.slide(wheelingSlideLength, true, false);

            //
            // Compute active page number.
            //

            const hiddenLeftItems = Math.abs(wheelingSlideLength) / itemWidth;
            let pageNumber = 0;

            if (this.isWheelingLeft) {
                pageNumber = Math.floor(hiddenLeftItems / itemsPerPage);
            } else {
                pageNumber = Math.ceil(hiddenLeftItems / itemsPerPage);
            }

            // Set active for the page number
            await this.setActivePaginationItem(pageNumber);
        } finally {
            this.clearWheelData();
        }
    }

    updateTouchData(touchPoint) {
        if (!touchPoint) {
            return;
        }

        if (!this.touchData) {
            this.touchData = {};
        }

        if (!this.touchData.startPoint) {
            this.touchData.startPoint = touchPoint;
        }

        this.touchData.currentPoint = touchPoint;

        //
        // Compute touch direction
        //

        const startPoint = this.touchData.startPoint;
        const endPoint = this.touchData.currentPoint;
        const deltaX = endPoint.clientX - startPoint.clientX;
        const deltaY = endPoint.clientY - startPoint.clientY;

        if (deltaX === 0 && deltaY === 0) {
            this.touchData.direction = '';
            this.touchData.distance = 0;
        } else if (deltaY < 0 && Math.abs(deltaY) >= Math.abs(deltaX)) {
            this.touchData.direction = 'up';
            this.touchData.distance = Math.abs(deltaY);
        } else if (deltaY > 0 && deltaY >= Math.abs(deltaX)) {
            this.touchData.direction = 'down';
            this.touchData.distance = deltaY;
        } else if (deltaX > 0 && deltaX > Math.abs(deltaY)) {
            this.touchData.direction = 'right';
            this.touchData.distance = deltaX;
        } else {
            this.touchData.direction = 'left';
            this.touchData.distance = Math.abs(deltaX);
        }
    }

    clearTouchData() {
        this.touchData = {};
    }

    updateWheelData(wheelEvent) {
        if (!wheelEvent) {
            return;
        }

        if (!this.wheelData) {
            this.wheelData = {};
        }

        this.wheelData.currentEvent = wheelEvent;

        const deltaX = wheelEvent.deltaX;
        const deltaY = wheelEvent.deltaY;

        if (deltaX === 0 && deltaY === 0) {
            this.wheelData.direction = '';
            this.wheelData.distance = 0;
        } else if (deltaY > 0 && deltaY > Math.abs(deltaX)) {
            this.wheelData.direction = 'up';
            this.wheelData.distance = deltaY;
        } else if (deltaX < 0 && Math.abs(deltaX) >= Math.abs(deltaY)) {
            this.wheelData.direction = 'right';
            this.wheelData.distance = Math.abs(deltaX);
        } else if (deltaY < 0 && Math.abs(deltaY) > Math.abs(deltaX)) {
            this.wheelData.direction = 'down';
            this.wheelData.distance = Math.abs(deltaY);
        } else {
            this.wheelData.direction = 'left';
            this.wheelData.distance = Math.abs(deltaX);
        }
    }

    clearWheelData() {
        this.wheelData = {};
    }

    /**
     * When user swiping on slide item
     */
    async onTouchMoving() {
        if (!this.isSwipingLeft && !this.isSwipingRight) {
            return;
        }

        // Stop the Slideshow if it is running
        // when the client start the swipe action.
        await this.stopSlideShow();

        this.slide(this.swipingSlideLength, false, false);
    }

    /**
     * When user end swiping on slide item
     */
    async onTouchEnd() {
        try {
            if (!this.isSwipingLeft && !this.isSwipingRight) {
                // Return to previous state if current
                // swiping action is not horizontal.
                this.slide(this.lastSlideLength);
                return;
            }

            this.slide(this.swipingSlideLength, true, false);

            const itemsPerPage = await this.itemsPerPage;
            const itemWidth = await this.itemWidth;
            const minSlideLength = await this.minSlideLength;
            const maxSlideLength = this.maxSlideLength;

            let endSwipingSlideLength = this.lastSlideLength;

            if (endSwipingSlideLength < minSlideLength) {
                endSwipingSlideLength = minSlideLength;
            } else if (endSwipingSlideLength > maxSlideLength) {
                endSwipingSlideLength = maxSlideLength;
            }

            let hiddenLeftItems = Math.abs(endSwipingSlideLength) / itemWidth;
            let pageNumber = 0;

            if (this.isSwipingLeft) {
                hiddenLeftItems = Math.ceil(hiddenLeftItems);
                pageNumber = Math.floor(hiddenLeftItems / itemsPerPage);
            } else {
                hiddenLeftItems = Math.floor(hiddenLeftItems);
                pageNumber = Math.ceil(hiddenLeftItems / itemsPerPage);
            }

            // Re-compute the endSwipingSlideLength value
            // to match hiddenLeftItems value.
            endSwipingSlideLength = -1 * hiddenLeftItems * itemWidth;

            // End the slide action
            this.slide(endSwipingSlideLength);

            // Active current page
            await this.setActivePaginationItem(pageNumber);
        } finally {
            this.clearTouchData();
        }
    }

    async handleIntersectionObserver(entries) {
        if (!entries || !Array.isArray(entries)) {
            return;
        }

        entries.forEach((observerEntry) => {
            if (observerEntry.target === this.element) {
                if (observerEntry.isIntersecting) {
                    this.startSlideShow();
                } else {
                    this.stopSlideShow();
                }
            }
        });
    }

    async slideToNextItem(currentTime) {
        const elapsedTime = currentTime - this.slideShowStartTime;

        if (elapsedTime >= this.slideShowDelay) {
            const itemsPerPage = await this.itemsPerPage;
            const itemWidth = await this.itemWidth;
            const minSlideLength = await this.minSlideLength;
            let lastSlideLength = this.lastSlideLength;

            let slideLength = lastSlideLength - itemWidth;

            //
            // If reach to the end of the slider box
            //
            if (slideLength < minSlideLength) {
                if (this.slideShowInfinity) {
                    // Return to the beginning of the slider box.
                    slideLength = this.maxSlideLength;
                } else {
                    // Finish the slideshow.
                    await this.stopSlideShow();
                    return;
                }
            }

            lastSlideLength = this.slide(slideLength);

            const hiddenLeftItems = Math.ceil(Math.abs(lastSlideLength) / itemWidth);
            const pageNumber = Math.floor(hiddenLeftItems / itemsPerPage);

            await this.setActivePaginationItem(pageNumber);

            //
            // Start a new delay period
            //
            this.slideShowStartTime = performance.now();
        }

        this.slideShowRequestId = requestAnimationFrame((timestamp) => {
            this.slideToNextItem(timestamp);
        });
    }

    async startSlideShow() {
        this.slideShowStartTime = performance.now();
        this.slideShowRequestId = requestAnimationFrame((timestamp) => {
            this.slideToNextItem(timestamp);
        });
    }

    async stopSlideShow() {
        if (this.slideShowRequestId !== null) {
            cancelAnimationFrame(this.slideShowRequestId);
            this.slideShowRequestId = null;
        }
    }
}
